import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/16x16/delete-trash.svg';
import {
  Alert,
  Button,
  Dialog,
  FlexColumn,
  GenerateAIButton,
  LoadingButton,
  Link,
  MultiStepDrawer,
  DrawerStep,
} from '@elementary/common-ui/components';
import { Divider, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { ENVIRONMENT } from '@elementary/common-ui/constants/queryKeys';
import useSupportChat from 'shared/hooks/useSupportChat';
import {
  EnvironmentMutationInput,
  toFormAssetSources,
} from 'shared/hooks/integrations/useEnvironmentsSetup';
import * as api from 'shared/services/settingsApi';
import { WarehouseType } from '@elementary/common-ui/types';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import {
  AthenaRoleArnConnectionCredentials,
  AthenaSecretAccessKeyConnectionCredentials,
  BigQueryAuthMethod,
  DatabricksAuthMethod,
  DatabricksOAuthConnectionCredentials,
  DatabricksStorageAccessType,
  DatabricksTokenConnectionCredentials,
  EnvironmentForm,
  FetchEnvironmentApi,
  SnowflakeKeyPairConnectionCredentials,
  SnowflakePasswordConnectionCredentials,
} from 'shared/types';
import { fontSize, space } from '@elementary/common-ui/style/utils';
import { EnvironmentError } from '../../../types';
import EnvironmentSetupStep from './EnvironmentSetupStep';
import EnvironmentContentStep from './EnvironmentContentStep';
import ConnectionErrorAlert from './ConnectionErrorAlert';
import SourceContentSection from './SourceContentSection';
import ConnectionHealthSection from './ConnectionHealthSection';
import {
  CONNECTION_STEP_INDEX,
  CONNECTION_STEP_TITLES,
  CONTENT_STEP_INDEX,
  ENVIRONMENT_CONTENT_STEP_TITLE,
  ENVIRONMENT_DRAWER_ACTIONS,
} from './constants';
import useEnvironmentFormUiContext from './uiContext/useEnvironmentFormUiContext';
import { getAuthMethodDefaults, getDefaultDbtAssetSource } from './utils';

const Form = styled.form`
  gap: inherit;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled(FlexColumn)`
  gap: ${space('xxl')};
`;

const getDefaultValues = (
  data: FetchEnvironmentApi,
): Partial<EnvironmentForm> => {
  const environmentName = data.name;
  const environmentType = data.environmentType;
  const warehouseType = data.dwhConnection.warehouseType as WarehouseType;
  const credentials = data.dwhConnection.credentials;

  const assetSources = toFormAssetSources(data.assetSources);

  let credentialsDefaultValues: Partial<EnvironmentForm> = {};

  switch (credentials?.type) {
    case 'snowflake':
      credentialsDefaultValues = {
        snowflakeAccount: credentials.account,
        snowflakeWarehouse: credentials.warehouse,
        snowflakeUserRole: credentials.role,
        snowflakeUser: credentials.user,
        snowflakePassword: (
          credentials as SnowflakePasswordConnectionCredentials
        ).password,
        snowflakePrivateKey: (
          credentials as SnowflakeKeyPairConnectionCredentials
        ).private_key,
        snowflakePrivateKeyPassphrase: (
          credentials as SnowflakeKeyPairConnectionCredentials
        ).private_key_passphrase,
      };
      break;
    case 'redshift':
      credentialsDefaultValues = {
        redshiftHost: credentials.host,
        redshiftUsername: credentials.user,
        redshiftPassword: credentials.password,
        redshiftPort: credentials.port,
        sshEnabled: !!data.dwhConnection.ssh,
        sshHost: data.dwhConnection.ssh?.host,
        sshPort: data.dwhConnection.ssh?.port,
        sshUsername: data.dwhConnection.ssh?.username,
      };
      break;
    case 'bigquery': {
      const isWif = credentials.method === 'external-account';
      credentialsDefaultValues = {
        bigqueryAuthMethod: isWif
          ? BigQueryAuthMethod.WIF
          : BigQueryAuthMethod.SERVICE_ACCOUNT,
        bigqueryAccountFile:
          !isWif && credentials.keyfile
            ? { name: credentials.keyfile }
            : undefined,
        bigqueryWifFile:
          isWif && credentials.external_account_file
            ? { name: credentials.external_account_file }
            : undefined,
        bigqueryLocation: credentials.location,
      };
      break;
    }
    case 'databricks': {
      const authType = credentials.auth_type;
      const storageAccess = credentials.storage_access;

      let storageAccessDefaults: Partial<EnvironmentForm> = {};
      if (storageAccess) {
        storageAccessDefaults = {
          databricksStorageAccessType: storageAccess.storage_access_type,
        };
        if (
          storageAccess.storage_access_type ===
          DatabricksStorageAccessType.ACCESS_KEYS
        ) {
          storageAccessDefaults.databricksStorageAccessKeyId =
            storageAccess.access_key_id;
          storageAccessDefaults.databricksStorageSecretAccessKey =
            storageAccess.secret_access_key;
          storageAccessDefaults.databricksStorageSessionToken =
            storageAccess.session_token;
        } else if (
          storageAccess.storage_access_type ===
          DatabricksStorageAccessType.ROLE_ARN
        ) {
          storageAccessDefaults.databricksStorageRoleArn =
            storageAccess.role_arn;
          storageAccessDefaults.databricksStorageExternalId =
            storageAccess.external_id;
        }
      }

      credentialsDefaultValues = {
        databricksHost: credentials.host,
        databricksHttpPath: credentials.http_path,
        databricksAuthMethod: authType,
        databricksAccessToken:
          authType === DatabricksAuthMethod.TOKEN
            ? (credentials as DatabricksTokenConnectionCredentials).token
            : undefined,
        databricksClientId:
          authType === DatabricksAuthMethod.OAUTH
            ? (credentials as DatabricksOAuthConnectionCredentials).client_id
            : undefined,
        databricksClientSecret:
          authType === DatabricksAuthMethod.OAUTH
            ? (credentials as DatabricksOAuthConnectionCredentials)
                .client_secret
            : undefined,
        ...storageAccessDefaults,
      };
      break;
    }
    case 'postgres':
      credentialsDefaultValues = {
        postgresHost: credentials.host,
        postgresPort: credentials.port,
        postgresUser: credentials.user,
        postgresPassword: credentials.password,
        sshEnabled: !!data.dwhConnection.ssh,
        sshHost: data.dwhConnection.ssh?.host,
        sshPort: data.dwhConnection.ssh?.port,
        sshUsername: data.dwhConnection.ssh?.username,
      };
      break;
    case 'athena':
      credentialsDefaultValues = {
        athenaRegion: credentials.region,
        athenaWorkgroup: credentials.workgroup,
        athenaS3StagingDir: credentials.s3_staging_dir,
        athenaAccessKeyId: (
          credentials as AthenaSecretAccessKeyConnectionCredentials
        ).aws_access_key_id,
        athenaSecretAccessKey: (
          credentials as AthenaSecretAccessKeyConnectionCredentials
        ).aws_secret_access_key,
        athenaRoleArn: (credentials as AthenaRoleArnConnectionCredentials)
          .aws_role_arn,
        athenaExternalId: (credentials as AthenaRoleArnConnectionCredentials)
          .aws_external_id,
      };
      break;
    case 'clickhouse':
      credentialsDefaultValues = {
        clickhouseHost: credentials.host,
        clickhousePort: credentials.port,
        clickhouseUser: credentials.user,
        clickhousePassword: credentials.password,
      };
      break;
    case 'dremio':
      credentialsDefaultValues = {
        dremioHost: credentials.host,
        dremioProjectId: credentials.project_id,
        dremioUser: credentials.user,
        dremioToken: credentials.token,
      };
      break;
    case 'fabric':
      credentialsDefaultValues = {
        fabricServer: credentials.server,
        fabricPort: credentials.port,
        fabricTenantId: credentials.tenant_id,
        fabricClientId: credentials.client_id,
        fabricClientSecret: credentials.client_secret,
      };
      break;
    default:
      break;
  }

  return {
    environmentName,
    environmentType,
    warehouseType,
    assetSources,
    ...credentialsDefaultValues,
    ...getAuthMethodDefaults(credentialsDefaultValues),
  };
};

type Props = {
  open: boolean;
  onSubmit: (data: EnvironmentMutationInput) => void;
  onClose: () => void;
  loading?: boolean;
  error?: EnvironmentError;
  environmentId?: string;
  onDeleteClick?: () => void;
};

const EnvironmentDrawer = ({
  open,
  environmentId,
  onSubmit,
  onClose,
  loading = false,
  error,
  onDeleteClick,
}: Props) => {
  const supportChat = useSupportChat();
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();
  const hasPermission = useAccountPermission();
  const canDeleteEnvironment = hasPermission('DELETE_ENVIRONMENTS');

  const [currentStep, setCurrentStep] = useState(CONNECTION_STEP_INDEX);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [formData, setFormData] = useState<EnvironmentForm>();

  const isEditMode = !!environmentId;

  const formRef = useRef<HTMLFormElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const shouldFetchExistingEnvironment = useMemo(
    () => isEditMode && open,
    [isEditMode, open],
  );

  const { data: environmentData, isLoading: environmentLoading } = useQuery(
    [ENVIRONMENT, environmentId],
    () => api.fetchEnvironment(environmentId!),
    { enabled: shouldFetchExistingEnvironment },
  );

  const handleSubmit = (data: EnvironmentForm) => {
    onSubmit({
      ...data,
      existingEnvironmentData: isEditMode ? environmentData : undefined,
    });
  };

  const defaultValues = useMemo(() => {
    if (environmentData) {
      return getDefaultValues(environmentData);
    }
    return {
      assetSources: [getDefaultDbtAssetSource('snowflake')],
      ...getAuthMethodDefaults({}),
    };
  }, [environmentData]);

  const existingAssetSourceIds = useMemo(
    () => environmentData?.assetSources.map((source) => source.id) || [],
    [environmentData],
  );

  const methods = useForm<EnvironmentForm>({
    defaultValues: defaultValues,
    mode: 'onChange',
  });

  const warehouseType = methods.watch('warehouseType');
  const assetSources = methods.watch('assetSources');

  useEnvironmentFormUiContext({
    methods,
    isOpen: open,
    currentStep,
    environmentId,
    existingAssetSourceIds,
    isSaving: loading,
    isLoadingEnvironment: shouldFetchExistingEnvironment && environmentLoading,
    error,
  });

  // Reset form when drawer opens/closes or data changes
  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
      setCurrentStep(CONNECTION_STEP_INDEX);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues]);

  // Scroll to error when it occurs
  useEffect(() => {
    if (error) {
      alertRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [error]);

  const handleFormSubmit = (vals: EnvironmentForm) => {
    if (isEditMode) {
      setFormData(vals);
      setConfirmDialogOpen(true);
    } else {
      handleSubmit(vals);
    }
  };

  const { isDirty } = methods.formState;

  const handleOpenAiChat = () => {
    openPanel({
      starterKey: isEditMode ? 'editEnvironment' : 'setupEnvironment',
      origin: 'ai_cta',
      mode: 'agent',
    });
  };

  const handleTopSave = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      handleFormSubmit(methods.getValues());
    }
  };

  const totalProjects =
    assetSources?.filter((source) => source.type === 'dbt').length || 0;
  const hasMultiProjectErrors =
    error?.dbtErrors?.dbtErrorsByDbSchemaKey &&
    Object.keys(error.dbtErrors?.dbtErrorsByDbSchemaKey || {}).length > 0;

  // Error display component
  const ErrorDisplay = () => {
    if (!error) return null;

    return (
      <div ref={alertRef}>
        {hasMultiProjectErrors ? (
          <ConnectionErrorAlert
            dbtErrorsByDbSchemaKey={
              error.dbtErrors?.dbtErrorsByDbSchemaKey || {}
            }
            allProjectsFailed={error.dbtErrors?.allProjectsFailed || false}
            totalProjects={totalProjects}
          />
        ) : (
          <Alert severity="error" title={error.message}>
            <span>
              If the problem persists, please{' '}
              <Link onClick={supportChat.show} href="#">
                contact us
              </Link>{' '}
              for further assistance.
            </span>
          </Alert>
        )}
      </div>
    );
  };

  // Danger Zone - only shown in edit mode
  const DangerZone = () => {
    if (!(isEditMode && canDeleteEnvironment && onDeleteClick)) {
      return null;
    }

    return (
      <FlexColumn sx={{ gap: 2, mt: 2 }}>
        <Divider />
        <Button
          variant="text"
          onClick={onDeleteClick}
          startIcon={<TrashIcon />}
          sx={{
            color: 'status.error.main',
            justifyContent: 'flex-start',
            p: 0,
            height: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
            '& svg path': {
              fill: 'currentColor',
            },
          }}
        >
          Delete environment
        </Button>
      </FlexColumn>
    );
  };

  const handleNavigateToContentStep = () => {
    setCurrentStep(CONTENT_STEP_INDEX);
  };

  // Step 1: Environment Setup
  const step1Content = (
    <StepContent>
      <EnvironmentSetupStep disabled={loading} />
      {isEditMode && (
        <SourceContentSection onConfigureClick={handleNavigateToContentStep} />
      )}
      {environmentId && (
        <>
          <Divider />
          <ConnectionHealthSection environmentId={environmentId} />
        </>
      )}
      <ErrorDisplay />
      <DangerZone />
    </StepContent>
  );

  // Step 2: dbt Projects
  const step2Content = warehouseType ? (
    <Form ref={formRef} onSubmit={methods.handleSubmit(handleFormSubmit)}>
      <StepContent>
        <EnvironmentContentStep
          warehouseType={warehouseType}
          existingAssetSourceIds={existingAssetSourceIds}
          disabled={loading}
        />
        <ErrorDisplay />
      </StepContent>
    </Form>
  ) : null;

  const validateStep1 = async (): Promise<boolean> => {
    // Trigger validation for all currently registered fields
    // Since step 2 content isn't rendered yet, only step 1 fields are validated
    // This automatically handles conditional fields (e.g., Snowflake password vs key-pair)
    const isValid = await methods.trigger();

    if (isValid && warehouseType) {
      if (!assetSources || assetSources.length === 0) {
        methods.setValue('assetSources', [
          getDefaultDbtAssetSource(warehouseType),
        ]);
      }
      return true;
    }
    return false;
  };

  const handleStep2Submit = async (): Promise<boolean> => {
    const isValid = await methods.trigger();
    if (isValid) {
      const vals = methods.getValues();
      handleFormSubmit(vals);
    }
    return false; // Always return false to prevent auto-navigation
  };

  const steps: DrawerStep[] = [
    {
      title: isEditMode
        ? CONNECTION_STEP_TITLES.edit
        : CONNECTION_STEP_TITLES.create,
      content: step1Content,
      submitButtonLabel: ENVIRONMENT_DRAWER_ACTIONS.continue,
      // In edit mode, saving happens via the header "Save changes" button, so
      // the footer "Continue" (which only makes sense in the setup wizard) is
      // hidden. Navigation to the asset-sources step is via "Configure asset
      // sources".
      hideSubmitButton: isEditMode,
      submitDisabled:
        !warehouseType ||
        loading ||
        (shouldFetchExistingEnvironment && environmentLoading),
      onStepSubmit: validateStep1,
    },
    {
      title: ENVIRONMENT_CONTENT_STEP_TITLE,
      content: step2Content,
      submitButtonLabel: loading
        ? ENVIRONMENT_DRAWER_ACTIONS.saving
        : isEditMode
        ? ENVIRONMENT_DRAWER_ACTIONS.save
        : ENVIRONMENT_DRAWER_ACTIONS.saveAndSync,
      // In edit mode, saving happens via the header "Save changes" button.
      // Setup mode keeps its footer "Save and sync" wizard action.
      hideSubmitButton: isEditMode,
      submitDisabled: loading,
      onStepSubmit: handleStep2Submit,
    },
  ];

  return (
    <>
      <Dialog
        open={confirmDialogOpen}
        customWidth={540}
        title="Update environment"
        confirmText={ENVIRONMENT_DRAWER_ACTIONS.saveChanges}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirmButtonClick={() => {
          setConfirmDialogOpen(false);
          handleSubmit(formData!);
        }}
        message={
          <Typography sx={{ fontSize: fontSize('sm') }}>
            Are you sure you want to update the environment configuration?
          </Typography>
        }
        secondaryMessage="In a few minutes you will be able to see the updated data in Elementary."
      />

      <FormProvider {...methods}>
        <MultiStepDrawer
          isOpen={open}
          onClose={onClose}
          steps={steps}
          isLoading={loading}
          isInitialLoading={
            shouldFetchExistingEnvironment && environmentLoading
          }
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          headerAction={
            <>
              {account?.ai_features_enabled && (
                <GenerateAIButton size="extraSmall" onClick={handleOpenAiChat}>
                  {isEditMode
                    ? ENVIRONMENT_DRAWER_ACTIONS.editWithAi
                    : ENVIRONMENT_DRAWER_ACTIONS.setupWithAi}
                </GenerateAIButton>
              )}
              {isEditMode && isDirty && (
                <LoadingButton
                  size="extraSmall"
                  onClick={handleTopSave}
                  loading={loading}
                >
                  {ENVIRONMENT_DRAWER_ACTIONS.saveChanges}
                </LoadingButton>
              )}
            </>
          }
        />
      </FormProvider>
    </>
  );
};

export default EnvironmentDrawer;
